/*
 * Saving a value that each change replaces whole (Benchmark's model lists in
 * `config/global.benchmarkCustomModels`, ux-admin-09). Without JSX or
 * relative imports so `scripts/lib/admin-latest-save.test.mjs` runs it with
 * Node.
 *
 * Saves run one after another, so the server ends with the last one that
 * succeeded and `persisted` always says what that is. A change made while
 * another is saving replaces the queued one instead of saving both. Only the
 * latest change decides what the screen shows: if it fails, the lists go back
 * to the last saved value and its error shows; an older change that fails
 * while a newer one is pending neither reverts nor shows an error, and an
 * older save that succeeds late never overwrites the newer state.
 */

export interface LatestSaver<T> {
  /** Saves `next` (the screen already shows it). Resolves once it and everything queued before it settled. */
  save(next: T): Promise<void>;
  /** What the server holds: the initial value or the last save that succeeded. */
  readonly persisted: T;
}

export interface LatestSaverOptions<T> {
  initial: T;
  write: (value: T) => Promise<unknown>;
  /** The latest change failed: show `persisted` again. */
  onRevert: (persisted: T) => void;
  /** The latest change failed with `error`. */
  onError: (error: unknown) => void;
}

export function createLatestSaver<T>({ initial, write, onRevert, onError }: LatestSaverOptions<T>): LatestSaver<T> {
  let persisted = initial;
  let latest = 0;
  let queue: Promise<void> = Promise.resolve();

  return {
    get persisted() {
      return persisted;
    },
    save(next: T): Promise<void> {
      const seq = ++latest;
      queue = queue.then(async () => {
        // A newer change was made while this one waited: it carries the whole value.
        if (seq !== latest) return;
        try {
          await write(next);
          persisted = next;
        } catch (error) {
          if (seq !== latest) return;
          onRevert(persisted);
          onError(error);
        }
      });
      return queue;
    },
  };
}
